import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./BusinessIntelligence.module.css";

interface Question {
  id: string;
  date: string;
  question: string;
}

const recentQuestions: Question[] = [
  {
    id: "1",
    date: "Mar. 3rd 2025",
    question: "How is my business doing",
  },
  {
    id: "2",
    date: "Mar. 2nd 2025",
    question:
      "What's the minimum temperature to open my outdoor patio profitably?",
  },
  {
    id: "3",
    date: "Mar. 1st 2025",
    question: "How does my energy usage compare to similar businesses nearby?",
  },
  {
    id: "4",
    date: "Feb. 23rd 2025",
    question:
      "Did my holiday promotion bring in more customers than last year?",
  },
  {
    id: "5",
    date: "Feb. 19th 2025",
    question: "Which days bring in the most customers?",
  },
];

export const BusinessIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"insights" | "recent">("insights");
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();

  const handleView = (questionId: string) => {
    console.log("View question:", questionId);
    // TODO: Navigate to question detail view
  };

  const handleAskQuestion = () => {
    console.log("Ask another question");
    // TODO: Open question input dialog
  };

  return (
    <div className={styles.businessIntelligence}>
      {/* Header with tabs */}
      <div className={styles.header}>
        <h1 className={styles.title}>{"Business Insights"}</h1>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "insights" ? styles.tabActive : styles.tabInactive
              }`}
            onClick={() => setActiveTab("insights")}
          >
            <svg
              className={styles.tabIcon}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 3.75L4.17 7.29L10 10.83L15.83 7.29L10 3.75Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1"
              />
              <circle cx="10" cy="10" r="1.5" fill="white" />
              <path
                d="M4.17 9v4.58L10 17.08L15.83 13.58V9"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            {"Get Insights"}
          </button>
          <button
            className={`${styles.tab} ${activeTab === "recent" ? styles.tabActive : styles.tabInactive
              }`}
            onClick={() => setActiveTab("recent")}
          >
            <svg
              className={styles.tabIcon}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 3C6.13 3 3 6.13 3 10s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm1 10H9V8h2v5z"
                fill="currentColor"
              />
            </svg>
            {"Recently Asked"}
          </button>
        </div>
      </div>

      {activeTab === "insights" ? (
        <div className={styles.insightsContent}>
          <button className={styles.askButton} onClick={handleAskQuestion}>
            {"Ask Another Question"}
          </button>

          <p className={styles.questionText}>
            You asked{" "}
            <em className={styles.questionHighlight}>
              " How is my business doing? "
            </em>
            "
          </p>

          <p className={styles.introText}>
            Based on the data provided for your store over the 3-day period from
            March 12 – 14 2025, here are some key observations about your
            business performance:
          </p>

          {/* Customer Traffic Section */}
          <div className={styles.insightCard}>
            <h2 className={styles.cardTitle}>{"Customer Traffic"}</h2>
            <div className={styles.cardContent}>
              <div className={styles.cardText}>
                <ul>
                  <li>
                    The number of people in the store peaked at 28-29 around 1
                    PM each day, then gradually declined into the evening hours.
                  </li>
                  <li>
                    Customer traffic patterns were quite consistent
                    day-over-day.
                  </li>
                </ul>
              </div>
              <div className={styles.chartContainer}>
                <h3 className={styles.chartTitle}>
                  Average Customer Traffic, March 12 – 14 2025
                </h3>
                <div className={styles.chartPlaceholder}>
                  <svg
                    width="100%"
                    height="200"
                    viewBox="0 0 500 200"
                    className={styles.chart}
                  >
                    {/* Y-axis lines */}
                    {[0, 10, 20, 30, 40, 50, 60].map((val, idx) => (
                      <g key={val}>
                        <line
                          x1="0"
                          y1={180 - idx * 30}
                          x2="480"
                          y2={180 - idx * 30}
                          stroke="rgba(0,0,47,0.15)"
                          strokeWidth="1"
                        />
                        <text
                          x="-5"
                          y={185 - idx * 30}
                          fontSize="12"
                          fill="#60646c"
                          textAnchor="end"
                        >
                          {val}
                        </text>
                      </g>
                    ))}
                    {/* Traffic line */}
                    <polyline
                      points="0,160 40,150 80,130 120,110 160,90 200,70 240,60 280,65 320,80 360,110 400,135 440,155 480,165"
                      fill="none"
                      stroke="#0090ff"
                      strokeWidth="2.5"
                    />
                  </svg>
                  <div className={styles.chartXAxis}>
                    {[
                      "7AM",
                      "8 AM",
                      "9 AM",
                      "10 AM",
                      "11 AM",
                      "12 PM",
                      "1 PM",
                      "2 PM",
                      "3 PM",
                      "4 PM",
                      "5 PM",
                      "6 PM",
                      "7 PM",
                    ].map((time) => (
                      <span key={time} className={styles.xAxisLabel}>
                        {time}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Staffing Section */}
          <div className={styles.insightCard}>
            <h2 className={styles.cardTitle}>{"Employee Staffing"}</h2>
            <div className={styles.cardContent}>
              <div className={styles.cardText}>
                <ul>
                  <li>
                    Staff numbers ramped up from 2 to 4 people clocked in by
                    midday, closely matching the customer traffic and
                    transaction curves.
                  </li>
                  <li>Consistent overstaffing occurs from 2:30 – 5:00 PM</li>
                  <li>
                    Overall, Staffing levels appear well-aligned to business
                    needs based on this limited data sample.
                  </li>
                </ul>
              </div>
              <div className={styles.chartContainer}>
                <h3 className={styles.chartTitle}>
                  Average Customer Traffic vs Employee Staffing, March 12 – 14
                  2025
                </h3>
                <div className={styles.chartPlaceholder}>
                  <svg
                    width="100%"
                    height="200"
                    viewBox="0 0 500 200"
                    className={styles.chart}
                  >
                    {/* Y-axis lines */}
                    {[0, 10, 20, 30, 40, 50, 60].map((val, idx) => (
                      <g key={val}>
                        <line
                          x1="0"
                          y1={180 - idx * 30}
                          x2="480"
                          y2={180 - idx * 30}
                          stroke="rgba(0,0,47,0.15)"
                          strokeWidth="1"
                        />
                        <text
                          x="-5"
                          y={185 - idx * 30}
                          fontSize="12"
                          fill="#60646c"
                          textAnchor="end"
                        >
                          {val}
                        </text>
                        <text
                          x="485"
                          y={185 - idx * 30}
                          fontSize="12"
                          fill="#60646c"
                        >
                          {idx * 2}
                        </text>
                      </g>
                    ))}
                    {/* Traffic line (blue) */}
                    <polyline
                      points="0,160 40,150 80,130 120,110 160,90 200,70 240,60 280,65 320,80 360,110 400,135 440,155 480,165"
                      fill="none"
                      stroke="#0090ff"
                      strokeWidth="2.5"
                    />
                    {/* Staffing line (dark) */}
                    <polyline
                      points="0,170 120,170 160,140 200,140 360,140 400,165 480,170"
                      fill="none"
                      stroke="#272f46"
                      strokeWidth="2.5"
                    />
                    {/* Overstaffed region (green) */}
                    <rect
                      x="280"
                      y="50"
                      width="120"
                      height="90"
                      fill="rgba(91, 185, 139, 0.2)"
                    />
                  </svg>
                  <div className={styles.chartXAxis}>
                    {[
                      "7AM",
                      "8 AM",
                      "9 AM",
                      "10 AM",
                      "11 AM",
                      "12 PM",
                      "1 PM",
                      "2 PM",
                      "3 PM",
                      "4 PM",
                      "5 PM",
                      "6 PM",
                      "7 PM",
                    ].map((time) => (
                      <span key={time} className={styles.xAxisLabel}>
                        {time}
                      </span>
                    ))}
                  </div>
                  <div className={styles.chartLegend}>
                    <div className={styles.legendItem}>
                      <span
                        className={styles.legendDot}
                        style={{ backgroundColor: "#0090ff" }}
                      ></span>
                      <span>{"Foot Traffic"}</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={styles.legendDot}
                        style={{ backgroundColor: "#272f46" }}
                      ></span>
                      <span>{"# of Employees"}</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={styles.legendSquare}
                        style={{ backgroundColor: "#5bb98b" }}
                      ></span>
                      <span>{"Overstaffed"}</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={styles.legendSquare}
                        style={{ backgroundColor: "#eb8e90" }}
                      ></span>
                      <span>{"Understaffed"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Environmental Factors Section */}
          <div className={styles.insightCard}>
            <h2 className={styles.cardTitle}>{"Environmental Factors"}</h2>
            <div className={styles.cardContent}>
              <div className={styles.cardText}>
                <ul>
                  <li>
                    Temperature, humidity, and light levels increased steadily
                    throughout each day, but did not appear to significantly
                    impact sales or traffic patterns.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Final Summary Section */}
          <div className={styles.insightCard}>
            <h2 className={styles.cardTitle}>{"Final Summary"}</h2>
            <div className={styles.cardContent}>
              <div className={styles.cardText}>
                <p>
                  Your store is showing strong operational consistency across
                  the three-day period. Sales, traffic, and transactions all
                  follow a stable daily rhythm with clear midday peaks around 1
                  PM. Conversion rates remain steady, and staffing levels are
                  well-matched to customer demand, suggesting efficient
                  workforce alignment. No major external factors appear to be
                  affecting performance. Overall, your business is running
                  smoothly, with consistent results and well-timed staffing
                  coverage — a solid baseline for identifying longer-term
                  patterns and opportunities for growth.
                </p>
              </div>
            </div>
          </div>

          <button
            className={styles.askButtonBottom}
            onClick={handleAskQuestion}
          >
            {"Ask Another Question"}
          </button>
        </div>
      ) : (
        <>
          {/* Search bar */}
          <div className={styles.searchContainer}>
            <div className={styles.searchInput}>
              <svg
                className={styles.searchIcon}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <circle
                  cx="8.5"
                  cy="8.5"
                  r="5.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M12.5 12.5L16 16"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                placeholder={"Search"}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchField}
              />
            </div>
            <button className={styles.filterButton}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 5h14M6 10h8M9 15h2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              {"Filter"}
            </button>
          </div>

          {/* Table */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>{"Date"}</th>
                  <th className={styles.tableHeader}>{"Question"}</th>
                  <th className={styles.tableHeader}></th>
                </tr>
              </thead>
              <tbody>
                {recentQuestions.map((item) => (
                  <tr key={item.id} className={styles.tableRow}>
                    <td className={styles.tableCell}>{item.date}</td>
                    <td className={styles.tableCellQuestion}>
                      <em>" {item.question} "</em>
                    </td>
                    <td className={styles.tableCellAction}>
                      <button
                        className={styles.viewButton}
                        onClick={() => handleView(item.id)}
                      >
                        {"View"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessIntelligence;
